import cn from 'classnames';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

import styles from './BrandInfoDescription.module.scss';

const COLLAPSED_LINES = 3;

const getLineHeightPx = (element: HTMLElement) => {
    const styles = window.getComputedStyle(element);
    const fontSize = parseFloat(styles.fontSize) || 14;
    const lineHeightValue = parseFloat(styles.lineHeight);

    return Number.isNaN(lineHeightValue) ? fontSize * 1.2 : lineHeightValue;
};

const Description = ({ children }: { children?: ReactNode }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showToggle, setShowToggle] = useState(false);

    useLayoutEffect(() => {
        const element = contentRef.current;

        if (!element || !children) {
            setShowToggle(false);

            return;
        }

        const measureOverflow = () => {
            const previousWebkitLineClamp = element.style.webkitLineClamp;
            const previousOverflow = element.style.overflow;

            // Measure full content height without clamp so short text does not show the button
            element.style.webkitLineClamp = 'unset';
            element.style.overflow = 'visible';

            const fullHeight = element.scrollHeight;
            const maxCollapsedHeight = getLineHeightPx(element) * COLLAPSED_LINES;

            element.style.webkitLineClamp = previousWebkitLineClamp;
            element.style.overflow = previousOverflow;

            setShowToggle(fullHeight > maxCollapsedHeight + 1);
        };

        measureOverflow();
        window.addEventListener('resize', measureOverflow);

        return () => {
            window.removeEventListener('resize', measureOverflow);
        };
    }, [children]);

    if (!children) {
        return null;
    }

    return (
        <div>
            <div
                ref={contentRef}
                className={cn(styles.description, isExpanded && styles.descriptionExpanded)}
                dangerouslySetInnerHTML={{ __html: String(children) }}
            />

            {showToggle && (
                <button
                    type="button"
                    data-testid="button-hidden"
                    onClick={() => setIsExpanded(prev => !prev)}
                    className={styles.toggle}
                >
                    {isExpanded ? 'Скрыть' : 'Читать'}
                </button>
            )}
        </div>
    );
};

export default Description;
